import React, { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import classNames from 'classnames';
import kebabCase from 'lodash/kebabCase';
import defaultTo from 'lodash/defaultTo';
import { Link } from 'react-router-dom';
import isEmail from 'validator/lib/isEmail';
import Button, { Appearance, LoadingButton } from '@atlaskit/button';
import { NEW_DATE_FORMAT } from '@atlassian/cssxp-typescommon';
import { abgGraphqlClient as graphqlClient } from '@atlassian/temp-client-utils';
import MoreIcon from '@atlaskit/icon/glyph/more';
import ExportIcon from '@atlaskit/icon/glyph/export';
import Popup from '@atlaskit/popup';
import { PopupMenuGroup, Section } from '@atlaskit/menu';
import { Stack } from '@atlaskit/primitives';
import { QuoteStatus as QuoteStatusType } from '@atlassian/cssxp-types-billing';
import { transformFilterableFields } from '@atlassian/cssxp-utils-generic';
import {
  ColGroup,
  CustomizeView,
  HeadCell,
  SearchResultColumn,
  TableBody,
  TableHead,
  TabPanel,
  Tabs,
  FormattedDate,
  CustomSearch,
  SortableTableHead,
  DateRangeType,
  useFeatureToggle,
  CustomFilterCheckboxWrapper,
} from '@css-xp/base';
import { CustomDynamicResultTable } from '@css-xp/base/custom-dynamic-result-table';
import {
  FilterOption,
  SortType,
  SortDirection,
  FetchInvoiceGroupsInQuoteTableQueryVariables,
  FetchInvoiceGroupsInQuoteTableQuery,
} from '@css-xp/graphtypes/src/generated-types-new';
import {
  DEFAULT_PAGE,
  DEFAULT_PAGE_SIZE,
  routePaths,
  ENABLE_DESIGN_ITERATION_1,
  maxThresholdForResults,
  SEARCH_PAGE_LOADER_OPTIONS,
  STATSIG_FEATURE_GATES,
} from '@css-xp/ppc-constants';
import {
  getRouteUrl,
  getSortDirectionMatchFromSortOptions,
  getSearchTextVariantsForASearchTerm,
  isExternallyBilled,
  areAllLineItemsExternallyBilled,
  shouldShowQuoteDownloadOptions,
} from '@css-xp/ppc-utils';
import {
  getEmptyStatePropQuoteTable,
  errorStateProp,
  getSelectedColumns,
  TableHeading,
  NoData,
  FormatCurrency,
  SoldToAddress,
  AtlassianEmail,
  BillingProfileNameFromId,
  LimitCurrencyDropdown,
} from '@css-xp/ppc-common';
import { emptyStatePropForQuoteTable, emptyStatePropForWrongSearch } from '@css-xp/ppc-common/src/state-props';
import { QuoteDetailsPopup, QuoteStatus } from '@css-xp/ppc-quote-status';
import { getPartnerServiceDeskUrlForNewQuoteHelp } from '@css-xp/ppc-navigation';
import { EndCustomerContacts, useFetchIdsFromContactEmail } from '@css-xp/contact-resources';
import { DiscountType, getTotalPartnerMarginAmount } from '@css-xp/ppc-quotes-page';
import { SoldToAddressDetailsLink } from '@css-xp/ppc-common/src/sold-to-address-details-link';
import { getStaleQuoteReasonAtHeaderLevel } from '@css-xp/ppc-quotes-page/src/components/quote-line-items/stale-quote-reasons';
import { BilledExternallyTag } from '@css-xp/ppc-billed-externally';
import { QuoteActions } from './components/quote-actions';
import { QuoteDateFilter } from './components/quote-date-filter';
import { QuoteDateAndTimeDuration } from './components/quote-date-and-time-duration';
import { useQuotesListCSV } from './use-quote-list-csv';
import { QuotePurchaseOrder } from './components/quote-purchase-order';
import { QuoteContact } from './components/quote-contact/quote-contact';
import { fetchInvoiceGroupsInQuoteTable } from './queries/fetch-invoice-groups-in-quote-table';
import './quote-table.scss';

export interface QuoteTableProps {
  dataState: {
    data: any;
    loading: boolean;
    refetch: any;
    error: string | undefined;
    totalCount: number;
  };
  transactionAccountId: string;
  label?: string;
  shouldHideTabs?: boolean;
  defaultSortOptions?: SortType;
  // hiddenColumns is used to hide columns from the table
  hiddenColumns?: QuoteTableColumnsType[];
  shouldHideActionsMenu?: boolean;
  defaultSearchFilterOptions?: FilterOption;
  csvFilterOptions?: FilterOption;
  shouldHideCustomiseView?: boolean;
  columns?: string[];
  shouldHideFilters?: boolean;
  isSearchTermPresent?: boolean;
  showBillingProfileNumberInBillingProfileColumn?: boolean;
  shouldDisableRequestQuoteCta?: boolean;
}

interface FormData {
  searchTerm: string;
}

type ColumnList = HeadCell & SearchResultColumn;

enum TAB_ENUM {
  // Used for the names and order of the tabs
  OPEN = 'Open',
  ACCEPTED = 'Accepted',
  CANCELLED = 'Cancelled',
}

enum QuotesStatusFilterOptions {
  OPEN = 'Open',
  NEEDS_REVIEW = 'Needs review',
}

enum QuotesStatusFilterOptionsWithoutTabs {
  OPEN = 'Open',
  NEEDS_REVIEW = 'Needs review',
  ACCEPTED = 'Accepted',
  CANCELLED = 'Cancelled',
  STALE = 'Stale',
}

export const COLUMNS = {
  QUOTE_ID: 'Quote Id',
  QUOTE_NUMBER: 'Quote number',
  STATUS: 'Status',
  QUOTE_TOTAL: 'Quote total',
  PARTNER_MARGIN: 'Partner discount',
  EXPIRY_DATE: 'Expiry date',
  EXPIRES: 'Expires',
  COMPANY_NAME: 'Company name',
  CUSTOMER: 'Customer',
  BILLING_PROFILE: 'Billing profile',
  SOLD_TO_ADDRESS: 'Sold-to address',
  CUSTOMER_CONTACTS: 'Customer contacts',
  LAST_UPDATED: 'Last updated',
  CREATED: 'Created',
  CREATED_BY: 'Created by',
  UPDATED_BY: 'Updated by',
  QUOTE_CONTACT: 'Quote contact',
  PO_NUMBER: 'PO number',
  ACTIONS: 'Actions',
};
export type QuoteTableColumnsType = keyof typeof COLUMNS;

export const defaultQuotesFilterOptions = {
  and: [
    {
      or: [
        {
          key: 'status',
          value: QuoteStatusType.OPEN,
        },
        {
          key: 'status',
          value: QuoteStatusType.STALE,
        },
      ],
    },
  ],
};

export const hiddenTabsQuoteFilterOptions = {
  and: [
    {
      or: [
        {
          key: 'status',
          value: QuoteStatusType.OPEN,
        },
        {
          key: 'status',
          value: QuoteStatusType.STALE,
        },
        {
          key: 'status',
          value: QuoteStatusType.ACCEPTED,
        },
        {
          key: 'status',
          value: QuoteStatusType.CANCELLED,
        },
      ],
    },
  ],
};

const defaultColumns = [
  COLUMNS.QUOTE_NUMBER,
  COLUMNS.STATUS,
  COLUMNS.QUOTE_TOTAL,
  COLUMNS.PARTNER_MARGIN,
  COLUMNS.CREATED,
  COLUMNS.EXPIRES,
  COLUMNS.CUSTOMER,
  COLUMNS.SOLD_TO_ADDRESS,
  COLUMNS.CREATED_BY,
  COLUMNS.UPDATED_BY,
  COLUMNS.CUSTOMER_CONTACTS,
  COLUMNS.QUOTE_CONTACT,
  COLUMNS.PO_NUMBER,
  COLUMNS.LAST_UPDATED,
];

const SORT_KEYS = {
  ID: 'id',
  CREATED_DATE: 'createdDate',
  EXPIRY_DATE: 'expiryDate',
  TOTAL_AMOUNT: 'estimatesTotalAmount',
  UPDATED_DATE: 'updatedDate',
  COMPANY_NAME: 'shipToDisplayName',
};

const expiryDateRangeOption = [
  { text: 'Next 24 hours', value: -1 },
  { text: 'Next 7 days', value: -7 },
  { text: 'Next 14 days', value: -14 },
  { text: 'Next 30 days', value: -30 },
  { text: 'Next 90 days', value: -90 },
  { text: 'Next 12 months', value: -365 },
  { text: 'Custom', value: 0 },
];

const DATE_RANGE_KEYS = {
  CREATED_DATE: 'createdDate',
  EXPIRY_DATE: 'expiryDate', // expiryDate is used in QuoteDateFilter as well, so change in both places if needed
  LAST_UPDATED: 'updatedDate',
};

const getCreatedSortType = (isDescSorted?: boolean): SortType => {
  return {
    sortFields: [SORT_KEYS.CREATED_DATE, SORT_KEYS.ID],
    sortFieldsDirections: [isDescSorted ? SortDirection.DESC : SortDirection.ASC, SortDirection.DESC],
  };
};

const getExpirySortType = (isAscSorted?: boolean): SortType => {
  return {
    sortFields: [SORT_KEYS.EXPIRY_DATE, SORT_KEYS.ID],
    sortFieldsDirections: [isAscSorted ? SortDirection.ASC : SortDirection.DESC, SortDirection.DESC],
  };
};

const getTotalAmountSortType = (isAscSorted?: boolean): SortType => {
  return {
    sortFields: [SORT_KEYS.TOTAL_AMOUNT, SORT_KEYS.ID],
    sortFieldsDirections: [isAscSorted ? SortDirection.ASC : SortDirection.DESC, SortDirection.DESC],
  };
};

const getUpdatedDateSortType = (isAscSorted?: boolean): SortType => {
  return {
    sortFields: [SORT_KEYS.UPDATED_DATE, SORT_KEYS.ID],
    sortFieldsDirections: [isAscSorted ? SortDirection.ASC : SortDirection.DESC, SortDirection.DESC],
  };
};

const getCompanySortType = (isAscSorted?: boolean): SortType => {
  return {
    sortFields: [SORT_KEYS.COMPANY_NAME, SORT_KEYS.ID],
    sortFieldsDirections: [isAscSorted ? SortDirection.ASC : SortDirection.DESC, SortDirection.DESC],
  };
};

const defaultIsCreatedDescSorted = true;

const initialSelectedFieldsForStatus: string[] = [];
const getInitialStatusFilterFields = (shouldHideTabs?: boolean, shouldAddDefaultSelected?: boolean) => {
  const initialStatusFilterFields = [
    {
      filterItem: 'Status',
      queryKey: 'status',
      iterableValues: transformFilterableFields(
        shouldHideTabs ? QuotesStatusFilterOptionsWithoutTabs : QuotesStatusFilterOptions
      ),
      defaultSelectedFields:
        shouldHideTabs && shouldAddDefaultSelected
          ? [QuotesStatusFilterOptionsWithoutTabs.OPEN, QuotesStatusFilterOptionsWithoutTabs.NEEDS_REVIEW]
          : initialSelectedFieldsForStatus,
    },
  ];

  return initialStatusFilterFields;
};

const statusFilterMap: Record<string, string> = {
  [QuotesStatusFilterOptions.OPEN]: QuoteStatusType.OPEN,
  [QuotesStatusFilterOptions.NEEDS_REVIEW]: QuoteStatusType.STALE,
  [QuotesStatusFilterOptionsWithoutTabs.STALE]: QuoteStatusType.STALE,
  [QuotesStatusFilterOptionsWithoutTabs.ACCEPTED]: QuoteStatusType.ACCEPTED,
  [QuotesStatusFilterOptionsWithoutTabs.CANCELLED]: QuoteStatusType.CANCELLED,
};

export const QuoteTable = ({
  dataState,
  transactionAccountId,
  label,
  shouldHideTabs = false,
  defaultSortOptions = getCreatedSortType(defaultIsCreatedDescSorted),
  hiddenColumns,
  shouldHideActionsMenu = false,
  defaultSearchFilterOptions,
  csvFilterOptions,
  shouldHideCustomiseView,
  columns = defaultColumns,
  shouldHideFilters,
  isSearchTermPresent,
  showBillingProfileNumberInBillingProfileColumn = false,
  shouldDisableRequestQuoteCta,
}: QuoteTableProps) => {
  const { data, loading, refetch, error, totalCount } = dataState;
  const [isEnabled] = useFeatureToggle();
  const isDesignIteration1Enabled = isEnabled(ENABLE_DESIGN_ITERATION_1);
  const isMultiCurrencyChangesEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_MULTI_CURRENCY_CHANGES);
  const isDefaultFiltersEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_DEFAULT_FILTERS);
  const [rowsPerPage, setRowsPerPage] = useState<number>(DEFAULT_PAGE_SIZE);
  const [currentPage, setCurrentPage] = useState<number>(DEFAULT_PAGE);
  const filterOptions = useRef<FilterOption>(
    defaultSearchFilterOptions ?? (shouldHideTabs && !isDefaultFiltersEnabled)
      ? hiddenTabsQuoteFilterOptions
      : defaultQuotesFilterOptions
  );
  const [selectedTab, setSelectedTab] = useState<string | undefined>(shouldHideTabs ? undefined : TAB_ENUM.OPEN);
  const [sortOptions, setSortOptions] = useState<SortType>(defaultSortOptions);
  const [searchInputText, setSearchInputText] = useState<string>('');
  const [createdDateRange, setCreatedDateRange] = useState<{ key: string; value: string }[]>([]);
  const [expiryDateRange, setExpiryDateRange] = useState<{ key: string; value: string }[]>([]);
  const [updatedDateRange, setUpdatedDateRange] = useState<{ key: string; value: string }[]>([]);
  const [selectedCreatedDateRangeValue, setSelectedCreatedDateRangeValue] = useState('');
  const [selectedExpiryDateRangeValue, setSelectedExpiryDateRangeValue] = useState('');
  const [selectedUpdatedDateRangeValue, setSelectedUpdatedDateRangeValue] = useState('');
  // Using `setStatusFilterResetCount` to reset the internal state for status filter component `CustomFilterCheckboxWrapper`
  const [statusFilterResetCount, setStatusFilterResetCount] = useState<number>(0);
  const [statusFilterFields, setStatusFilterFields] = useState(
    getInitialStatusFilterFields(shouldHideTabs, isDefaultFiltersEnabled)
  );
  const [statusFilterOptions, setStatusFilterOptions] = useState<Record<string, string>[]>(
    shouldHideTabs
      ? isDefaultFiltersEnabled
        ? [
            { status: QuotesStatusFilterOptionsWithoutTabs.OPEN },
            { status: QuotesStatusFilterOptionsWithoutTabs.STALE },
          ]
        : [
            { status: QuoteStatusType.OPEN },
            { status: QuoteStatusType.STALE },
            { status: QuoteStatusType.ACCEPTED },
            { status: QuoteStatusType.CANCELLED },
          ]
      : [{ status: QuoteStatusType.OPEN }, { status: QuoteStatusType.STALE }]
  );
  const [isStatusFilterAppliedByDefault, setIsStatusFilterAppliedByDefault] = useState<boolean>(
    isDesignIteration1Enabled && isDefaultFiltersEnabled
  );
  const [isStatusFilterApplied, setIsStatusFilterApplied] = useState<boolean>(false);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [currency, setCurrency] = useState<string>('');

  const { downloadQuotesListCSV, quotesListLoading } = useQuotesListCSV({
    data: dataState?.data,
    totalCount: dataState?.totalCount,
    filterOptions: csvFilterOptions,
    txaId: transactionAccountId,
    sortOptions: sortOptions,
  });

  const { getResourceIdsFilterOptions, isIdsLoading, fetchIdsError, setFetchIdsError } = useFetchIdsFromContactEmail({
    transactionAccountId: transactionAccountId,
  });

  const hasNoResults = totalCount === 0;

  useEffect(() => {
    if (defaultSearchFilterOptions) {
      filterOptions.current = defaultSearchFilterOptions;
    }
  }, [defaultSearchFilterOptions]);

  React.useEffect(() => {
    if (loading || !isDesignIteration1Enabled || !isDefaultFiltersEnabled) return;

    // status filter will be applied by default which will show filter container
    // we should hide filter container when there's an error
    if (isStatusFilterAppliedByDefault) {
      if (error) {
        setIsStatusFilterAppliedByDefault(false);
      }
    }
  }, [isDesignIteration1Enabled, isStatusFilterAppliedByDefault, error, totalCount, loading, isDefaultFiltersEnabled]);

  const isCreatedDescSortedRef = useRef<boolean | undefined>(
    getSortDirectionMatchFromSortOptions(defaultSortOptions, SORT_KEYS.CREATED_DATE, SortDirection.DESC)
  );
  const isExpiryAscSortedRef = useRef<boolean | undefined>(
    getSortDirectionMatchFromSortOptions(defaultSortOptions, SORT_KEYS.EXPIRY_DATE, SortDirection.ASC)
  );
  const isTotalAmountAscSortedRef = useRef<boolean | undefined>(
    getSortDirectionMatchFromSortOptions(defaultSortOptions, SORT_KEYS.TOTAL_AMOUNT, SortDirection.ASC)
  );
  const isUpdatedDateAscSortedRef = useRef<boolean | undefined>(
    getSortDirectionMatchFromSortOptions(defaultSortOptions, SORT_KEYS.UPDATED_DATE, SortDirection.ASC)
  );
  const isCompanyNameAscSortedRef = useRef<boolean | undefined>(
    getSortDirectionMatchFromSortOptions(defaultSortOptions, SORT_KEYS.COMPANY_NAME, SortDirection.ASC)
  );

  const isCurrencyFilterApplied = useMemo(() => {
    return isMultiCurrencyChangesEnabled && currency !== '';
  }, [currency, isMultiCurrencyChangesEnabled]);

  const CreatedHead = () => {
    const handleSorting = () => {
      isCreatedDescSortedRef.current = !isCreatedDescSortedRef.current;
      isExpiryAscSortedRef.current = undefined;
      isTotalAmountAscSortedRef.current = undefined;
      isUpdatedDateAscSortedRef.current = undefined;
      isCompanyNameAscSortedRef.current = undefined;
      const newSortOptions: SortType = getCreatedSortType(isCreatedDescSortedRef.current);

      setSortOptions(newSortOptions);
      setCurrentPage(DEFAULT_PAGE);
      refetch({
        filterOptions: filterOptions.current,
        searchOptions: {
          first: rowsPerPage,
          offset: (DEFAULT_PAGE - 1) * rowsPerPage,
          sort: newSortOptions,
        },
      });
    };

    // Handling the sortable field which can only take Ascending value related data
    // Adding handling for method to render the column sort order properly
    const sortableFieldAscValue =
      isCreatedDescSortedRef.current !== undefined ? !isCreatedDescSortedRef.current : undefined;

    return (
      <SortableTableHead
        isAscSorted={sortableFieldAscValue}
        handleSorting={handleSorting}
        columnName={COLUMNS.CREATED}
      />
    );
  };

  const ExpiryHead = ({ columnName }: { columnName: string }) => {
    const handleSorting = () => {
      isExpiryAscSortedRef.current = !isExpiryAscSortedRef.current;
      isCreatedDescSortedRef.current = undefined;
      isTotalAmountAscSortedRef.current = undefined;
      isUpdatedDateAscSortedRef.current = undefined;
      isCompanyNameAscSortedRef.current = undefined;
      const newSortOptions = getExpirySortType(isExpiryAscSortedRef.current);

      setCurrentPage(DEFAULT_PAGE);
      setSortOptions(newSortOptions);
      refetch({
        filterOptions: filterOptions.current,
        searchOptions: {
          first: rowsPerPage,
          offset: (DEFAULT_PAGE - 1) * rowsPerPage,
          sort: newSortOptions,
        },
      });
    };

    return (
      <SortableTableHead
        isAscSorted={isExpiryAscSortedRef.current}
        handleSorting={handleSorting}
        columnName={columnName}
      />
    );
  };
  const TotalAmountHead = () => {
    const handleSorting = () => {
      isTotalAmountAscSortedRef.current = !isTotalAmountAscSortedRef.current;
      isCreatedDescSortedRef.current = undefined;
      isExpiryAscSortedRef.current = undefined;
      isUpdatedDateAscSortedRef.current = undefined;
      isCompanyNameAscSortedRef.current = undefined;
      const newSortOptions = getTotalAmountSortType(isTotalAmountAscSortedRef.current);

      setCurrentPage(DEFAULT_PAGE);
      setSortOptions(newSortOptions);
      refetch({
        filterOptions: filterOptions.current,
        searchOptions: {
          first: rowsPerPage,
          offset: (DEFAULT_PAGE - 1) * rowsPerPage,
          sort: newSortOptions,
        },
      });
    };

    return (
      <SortableTableHead
        isAscSorted={isTotalAmountAscSortedRef.current}
        handleSorting={handleSorting}
        columnName={COLUMNS.QUOTE_TOTAL}
        className={isDesignIteration1Enabled ? 'text-align-right' : ''}
      />
    );
  };

  const UpdatedDateHead = () => {
    const handleSorting = () => {
      isUpdatedDateAscSortedRef.current = !isUpdatedDateAscSortedRef.current;
      isCreatedDescSortedRef.current = undefined;
      isExpiryAscSortedRef.current = undefined;
      isTotalAmountAscSortedRef.current = undefined;
      isCompanyNameAscSortedRef.current = undefined;
      const newSortOptions = getUpdatedDateSortType(isUpdatedDateAscSortedRef.current);

      setCurrentPage(DEFAULT_PAGE);
      setSortOptions(newSortOptions);
      refetch({
        filterOptions: filterOptions.current,
        searchOptions: {
          first: rowsPerPage,
          offset: (DEFAULT_PAGE - 1) * rowsPerPage,
          sort: newSortOptions,
        },
      });
    };

    return (
      <SortableTableHead
        isAscSorted={isUpdatedDateAscSortedRef.current}
        handleSorting={handleSorting}
        columnName={COLUMNS.LAST_UPDATED}
        shouldReverseSortIcon={true}
      />
    );
  };

  const CompanyHead = ({ columnName }: { columnName: string }) => {
    const handleSorting = () => {
      isCompanyNameAscSortedRef.current = !isCompanyNameAscSortedRef.current;
      isUpdatedDateAscSortedRef.current = undefined;
      isCreatedDescSortedRef.current = undefined;
      isExpiryAscSortedRef.current = undefined;
      isTotalAmountAscSortedRef.current = undefined;
      const newSortOptions = getCompanySortType(isCompanyNameAscSortedRef.current);

      setCurrentPage(DEFAULT_PAGE);
      setSortOptions(newSortOptions);
      refetch({
        filterOptions: filterOptions.current,
        searchOptions: {
          first: rowsPerPage,
          offset: (DEFAULT_PAGE - 1) * rowsPerPage,
          sort: newSortOptions,
        },
      });
    };

    return (
      <SortableTableHead
        isAscSorted={isCompanyNameAscSortedRef.current}
        handleSorting={handleSorting}
        columnName={columnName}
      />
    );
  };

  const headersMap = useMemo(() => {
    const renderPartnerMargin = () => {
      if (isDesignIteration1Enabled) {
        return <th className="text-align-right">{COLUMNS.PARTNER_MARGIN}</th>;
      }
    };
    return {
      [COLUMNS.QUOTE_NUMBER]: {
        name: COLUMNS.QUOTE_NUMBER,
        content: COLUMNS.QUOTE_NUMBER,
        customClassNames: 'col-quote-number',
        renderComponent: <th className="quote-number-cell">{COLUMNS.QUOTE_NUMBER}</th>,
      },
      [COLUMNS.STATUS]: {
        name: COLUMNS.STATUS,
        content: COLUMNS.STATUS,
        customClassNames: 'col-status',
      },
      [COLUMNS.QUOTE_TOTAL]: {
        name: COLUMNS.QUOTE_TOTAL,
        content: COLUMNS.QUOTE_TOTAL,
        customClassNames: 'col-common col-quote-total',
        renderComponent: <TotalAmountHead />,
      },
      [COLUMNS.BILLING_PROFILE]: {
        name: COLUMNS.BILLING_PROFILE,
        content: COLUMNS.BILLING_PROFILE,
        customClassNames: 'col-common col-billing-profile',
      },
      [COLUMNS.PARTNER_MARGIN]: {
        name: COLUMNS.PARTNER_MARGIN,
        content: COLUMNS.PARTNER_MARGIN,
        customClassNames: 'col-common',
        renderComponent: renderPartnerMargin(),
      },
      [COLUMNS.EXPIRY_DATE]: {
        name: COLUMNS.EXPIRY_DATE,
        content: COLUMNS.EXPIRY_DATE,
        customClassNames: 'col-common col-expiry-date',
        renderComponent: <ExpiryHead columnName={COLUMNS.EXPIRY_DATE} />,
      },
      [COLUMNS.EXPIRES]: {
        name: COLUMNS.EXPIRES,
        content: COLUMNS.EXPIRES,
        customClassNames: 'col-common col-expiry-date',
        renderComponent: <ExpiryHead columnName={COLUMNS.EXPIRES} />,
      },
      [COLUMNS.COMPANY_NAME]: {
        name: COLUMNS.COMPANY_NAME,
        content: COLUMNS.COMPANY_NAME,
        customClassNames: 'col-common',
        renderComponent: <CompanyHead columnName={COLUMNS.COMPANY_NAME} />,
      },
      [COLUMNS.CUSTOMER]: {
        name: COLUMNS.CUSTOMER,
        content: COLUMNS.CUSTOMER,
        customClassNames: 'col-common col-customer',
        renderComponent: <CompanyHead columnName={COLUMNS.CUSTOMER} />,
      },
      [COLUMNS.SOLD_TO_ADDRESS]: {
        name: COLUMNS.SOLD_TO_ADDRESS,
        content: COLUMNS.SOLD_TO_ADDRESS,
        customClassNames: 'col-sold-to-address',
        isMandatory: false,
        isChecked: true,
      },
      [COLUMNS.CUSTOMER_CONTACTS]: {
        name: COLUMNS.CUSTOMER_CONTACTS,
        content: COLUMNS.CUSTOMER_CONTACTS,
        customClassNames: 'col-customer-contact',
        isMandatory: false,
        isChecked: true,
      },
      [COLUMNS.LAST_UPDATED]: {
        name: COLUMNS.LAST_UPDATED,
        content: COLUMNS.LAST_UPDATED,
        customClassNames: 'col-common',
        isMandatory: false,
        isChecked: true,
        renderComponent: <UpdatedDateHead />,
      },
      [COLUMNS.CREATED]: {
        name: COLUMNS.CREATED,
        content: COLUMNS.CREATED,
        customClassNames: 'col-common',
        renderComponent: <CreatedHead />,
      },
      [COLUMNS.CREATED_BY]: {
        name: COLUMNS.CREATED_BY,
        content: COLUMNS.CREATED_BY,
        customClassNames: 'col-common',
        isMandatory: false,
        isChecked: true,
      },
      [COLUMNS.UPDATED_BY]: {
        name: COLUMNS.UPDATED_BY,
        content: COLUMNS.UPDATED_BY,
        customClassNames: 'col-common',
        isMandatory: false,
        isChecked: true,
      },
      [COLUMNS.QUOTE_CONTACT]: {
        name: COLUMNS.QUOTE_CONTACT,
        content: COLUMNS.QUOTE_CONTACT,
        customClassNames: 'col-quote-contact',
      },
      [COLUMNS.PO_NUMBER]: {
        name: COLUMNS.PO_NUMBER,
        content: COLUMNS.PO_NUMBER,
        customClassNames: 'col-common',
      },
      [COLUMNS.ACTIONS]: {
        name: COLUMNS.ACTIONS,
        content: COLUMNS.ACTIONS,
        customClassNames: 'col-actions',
      },
    };
  }, []);

  const getHeaders = () => {
    let allHeaders = [];

    if (isDesignIteration1Enabled) {
      allHeaders = columns.map((column) => {
        //@ts-ignore
        return headersMap[column];
      });
    } else {
      allHeaders = [
        headersMap[COLUMNS.QUOTE_NUMBER],
        headersMap[COLUMNS.STATUS],
        headersMap[COLUMNS.QUOTE_TOTAL],
        headersMap[COLUMNS.PARTNER_MARGIN],
        headersMap[COLUMNS.EXPIRY_DATE],
        headersMap[COLUMNS.COMPANY_NAME],
        headersMap[COLUMNS.SOLD_TO_ADDRESS],
        headersMap[COLUMNS.CUSTOMER_CONTACTS],
        headersMap[COLUMNS.LAST_UPDATED],
        headersMap[COLUMNS.CREATED],
        headersMap[COLUMNS.CREATED_BY],
        headersMap[COLUMNS.UPDATED_BY],
        headersMap[COLUMNS.QUOTE_CONTACT],
        headersMap[COLUMNS.PO_NUMBER],
      ];
    }

    const filteredHeaders = allHeaders.filter(
      (header) => !hiddenColumns?.includes(header.name as QuoteTableColumnsType)
    );

    return filteredHeaders;
  };

  const getFilterOptions = useCallback(
    async (
      searchInput?: string,
      selectedTab?: string,
      allDateRanges?: { key: string; value: string }[],
      selectedStatusFilter?: { key: string; value: string }[] | Record<string, string>[],
      emailFilterOptions?:
        | {
            key: string;
            values: string[];
          }[]
        | null,
      selectedCurrency?: string
      // eslint-disable-next-line max-params
    ) => {
      // Keep existing date ranges so that if new date range is selected, it can be appended to existing filters.
      // For example: If createdDate filter is already applied and then expiryDate filter is applied, both should get applied
      // instead of replacing the older one with the latest value.
      // If no date range (allDateRanges) is sent, then it means it is either not needed or is expected to be removed.
      const andFilter =
        (allDateRanges ?? []).length > 0 ? [...createdDateRange, ...expiryDateRange, ...updatedDateRange] : [];
      const newFilterOptions: FilterOption = {
        and: andFilter,
        or: [],
        not: [],
      };
      const tabToFilterKeyMap: Record<string, string> = {
        [TAB_ENUM.OPEN]: QuoteStatusType.OPEN,
        [TAB_ENUM.ACCEPTED]: QuoteStatusType.ACCEPTED,
        [TAB_ENUM.CANCELLED]: QuoteStatusType.CANCELLED,
      };

      if (defaultSearchFilterOptions) {
        if (newFilterOptions.and) {
          defaultSearchFilterOptions.and && newFilterOptions.and.push(...defaultSearchFilterOptions.and);
          defaultSearchFilterOptions.or && newFilterOptions.and.push({ or: defaultSearchFilterOptions.or });
        }
        if (newFilterOptions.not) {
          defaultSearchFilterOptions.not && newFilterOptions.not.push(...defaultSearchFilterOptions.not);
        }
      }
      if (!shouldHideTabs && selectedTab) {
        if (newFilterOptions.and && selectedTab === TAB_ENUM.OPEN) {
          newFilterOptions.and.push(...defaultQuotesFilterOptions.and);
          setStatusFilterFields(getInitialStatusFilterFields(shouldHideTabs));
        } else if (newFilterOptions.and) {
          newFilterOptions.and.push({
            key: 'status',
            value: tabToFilterKeyMap[selectedTab],
          });
          setStatusFilterFields([]);
        }
      } else if (!isDefaultFiltersEnabled) {
        if (newFilterOptions.and) {
          newFilterOptions.and.push(...hiddenTabsQuoteFilterOptions.and);
          setStatusFilterFields(getInitialStatusFilterFields(shouldHideTabs));
        }
      }

      if (selectedStatusFilter && selectedStatusFilter?.length > 0) {
        if (newFilterOptions.and) {
          const statusFilter: FilterOption[] = [];
          selectedStatusFilter.forEach((selectedFilter) => {
            const key = 'status';
            // @ts-ignore
            const value = statusFilterMap[selectedFilter?.status];
            if (value) {
              statusFilter.push({ key, value });
            }
          });
          if (statusFilter.length > 0) {
            newFilterOptions.and.push({ or: statusFilter });
          }
        }
        setStatusFilterOptions(selectedStatusFilter);
      } else if (isDesignIteration1Enabled && !selectedStatusFilter?.length && isDefaultFiltersEnabled) {
        // user must have unchecked everything from status filter or cleared search, and we don't need to fetch DELETED quotes
        newFilterOptions?.and?.push(...hiddenTabsQuoteFilterOptions.and);

        setStatusFilterOptions([
          { status: QuotesStatusFilterOptionsWithoutTabs.OPEN },
          { status: QuotesStatusFilterOptionsWithoutTabs.STALE },
          { status: QuotesStatusFilterOptionsWithoutTabs.ACCEPTED },
          { status: QuotesStatusFilterOptionsWithoutTabs.CANCELLED },
        ]);
        setStatusFilterFields(getInitialStatusFilterFields(shouldHideTabs));
      }

      if (searchInput && newFilterOptions.or) {
        if (emailFilterOptions) {
          newFilterOptions.or = [...newFilterOptions.or, ...emailFilterOptions];
        } else {
          const shipToDisplayNameKeys = [...getSearchTextVariantsForASearchTerm(searchInput)].map((item) => ({
            key: 'shipToDisplayName',
            value: item,
          }));

          newFilterOptions.or.push(
            {
              key: 'shipToAddress1',
              value: searchInput,
            },
            {
              key: 'shipToAddress2',
              value: searchInput,
            },
            {
              key: 'quoteDescription',
              value: searchInput,
            },
            {
              key: 'id',
              value: searchInput,
            },
            {
              key: 'quoteNumber',
              value: searchInput,
            },
            ...shipToDisplayNameKeys
          );
        }
      }

      [...(allDateRanges ?? [])].forEach((dateRange) => {
        const indexOfExistingItem = [...createdDateRange, ...expiryDateRange, ...updatedDateRange].findIndex(
          (item) => item.key === dateRange.key
        );

        // If applied keys are already present in the `and` filter, replace it with new values otherwise append it
        if (indexOfExistingItem > -1 && newFilterOptions.and) {
          newFilterOptions.and[indexOfExistingItem] = {
            key: dateRange.key,
            value: dateRange.value,
          };
        } else {
          newFilterOptions.and?.push({
            key: dateRange.key,
            value: dateRange.value,
          });
        }
      });

      if (isMultiCurrencyChangesEnabled && selectedCurrency && newFilterOptions.and) {
        newFilterOptions.and?.push({
          key: 'paymentCurrency',
          value: selectedCurrency,
        });
      }

      return newFilterOptions;
    },
    [
      createdDateRange,
      expiryDateRange,
      updatedDateRange,
      defaultSearchFilterOptions,
      isMultiCurrencyChangesEnabled,
      shouldHideTabs,
      isDesignIteration1Enabled,
      isDefaultFiltersEnabled,
    ]
  );

  const [columnsList, setColumnList] = useState<Array<ColumnList>>(getHeaders());
  const getQuotesRoute = (quoteId: string) => {
    const quotesRoute =
      transactionAccountId &&
      getRouteUrl(routePaths.ppcQuoteDetails, { txaId: transactionAccountId, quoteId: quoteId });

    return quotesRoute;
  };

  const isNeedsReviewStatus = (status: QuoteStatusType) => {
    return status === QuoteStatusType.STALE;
  };

  const getRowData = (item: any) => {
    const columns: (HeadCell & SearchResultColumn)[] = getSelectedColumns(columnsList);
    const rowData = columns.map((column) => {
      const {
        id,
        quoteNumber,
        status,
        name,
        createdDate,
        expiryDate,
        updatedDate,
        total,
        shipToObject,
        currency,
        quoteAuthor,
        updatedBy,
        billingGroupId,
      } = item;

      const externallyBilled = isExternallyBilled(item?.lineItems);
      const allItemsExternallyBilled = areAllLineItemsExternallyBilled(item?.lineItems);
      const renderRowActions = () => {
        const showActions = shouldShowQuoteDownloadOptions(status);
        return (
          showActions && (
            <QuoteActions id={id} transactionAccountId={transactionAccountId} quoteNumber={quoteNumber}></QuoteActions>
          )
        );
      };

      switch (column.name) {
        case COLUMNS.QUOTE_NUMBER:
          return (
            <td
              className={classNames('quote-number-cell', {
                'quote-externally-billed': externallyBilled,
              })}
            >
              <span className="flex align-center">
                {isNeedsReviewStatus(status) && (
                  <span className={classNames('quote-warning-icon-container quote-warning-container-popup')}>
                    <QuoteDetailsPopup
                      size="small"
                      titleContent={getStaleQuoteReasonAtHeaderLevel(item, null)}
                      showStaleQuoteReasonAtListLevel={true}
                    />
                  </span>
                )}
                <span className="quote-number-content">
                  {name && (
                    <Link to={`${getQuotesRoute(id)}`} data-testid="quote-name-link" className="quote-name flex-column">
                      <span>{name}</span>
                    </Link>
                  )}
                  <Stack>
                    <Link
                      to={`${getQuotesRoute(id)}`}
                      data-testid="quote-number-link"
                      className={classNames({ 'quote-num': !!name, 'quote-num-primary': !name })}
                    >
                      {quoteNumber}
                    </Link>
                    {externallyBilled && <BilledExternallyTag marginLeft={'0'} />}
                  </Stack>
                </span>
                {isDesignIteration1Enabled && renderRowActions()}
              </span>
            </td>
          );
        case COLUMNS.STATUS:
          return (
            <td>
              <div className="status-wrapper">
                <QuoteStatus status={status} size="small" hideWarningIcon={true} />
                {isDesignIteration1Enabled || renderRowActions()}
              </div>
            </td>
          );
        case COLUMNS.CREATED:
          return <td>{<FormattedDate date={createdDate} format={NEW_DATE_FORMAT} />}</td>;
        case COLUMNS.EXPIRES:
        case COLUMNS.EXPIRY_DATE:
          return (
            <td>
              {isDesignIteration1Enabled ? (
                <FormattedDate date={expiryDate} format={NEW_DATE_FORMAT} />
              ) : (
                <QuoteDateAndTimeDuration date={expiryDate} dateType="expiry-date" />
              )}
            </td>
          );
        case COLUMNS.LAST_UPDATED:
          return <td>{updatedDate ? <FormattedDate date={updatedDate} format={NEW_DATE_FORMAT} /> : <NoData />}</td>;
        case COLUMNS.QUOTE_TOTAL:
          if (allItemsExternallyBilled) {
            return <td>{<NoData />}</td>;
          }

          return (
            <td className={classNames({ 'text-align-right': isDesignIteration1Enabled })}>
              <FormatCurrency showCommas value={total} currency={currency} showCurrencyCode />
            </td>
          );
        case COLUMNS.BILLING_PROFILE:
          return (
            <td>
              <BillingProfileNameFromId
                id={billingGroupId}
                txaId={transactionAccountId}
                showBillingProfileNumber={showBillingProfileNumberInBillingProfileColumn}
              />
            </td>
          );
        case COLUMNS.CUSTOMER:
        case COLUMNS.COMPANY_NAME:
          return (
            <td>
              <SoldToAddressDetailsLink txaId={transactionAccountId} companyName={shipToObject?.displayName ?? ''}>
                {shipToObject?.displayName ? (
                  <span className="ship-to-company-name">{shipToObject?.displayName}</span>
                ) : (
                  <NoData />
                )}
              </SoldToAddressDetailsLink>
            </td>
          );
        case COLUMNS.SOLD_TO_ADDRESS:
          return (
            <td>
              <SoldToAddress
                txaId={transactionAccountId}
                shipTo={{
                  id: shipToObject?.id,
                  addressLine1: shipToObject?.addressLine1,
                  addressLine2: shipToObject?.addressLine2,
                  city: shipToObject?.city,
                  country: shipToObject?.country,
                  postcode: shipToObject?.postalCode,
                  state: shipToObject?.state,
                }}
                shouldDisableLink={isDesignIteration1Enabled}
              />
            </td>
          );
        case COLUMNS.CUSTOMER_CONTACTS:
          return (
            <td>
              <div>
                <EndCustomerContacts invoiceGroupId={billingGroupId} transactionAccountId={transactionAccountId} />
              </div>
            </td>
          );
        case COLUMNS.PO_NUMBER:
          return (
            <td>
              <QuotePurchaseOrder invoiceGroupId={billingGroupId} />
            </td>
          );
        case COLUMNS.QUOTE_CONTACT:
          return (
            <td>
              <QuoteContact quoteId={id} transactionAccountId={transactionAccountId} />
            </td>
          );
        case COLUMNS.CREATED_BY:
          return (
            <td
              className="text-ellipsis"
              data-testid="quotes-created-by"
              title={!quoteAuthor?.includes('atlassian.com') && quoteAuthor}
            >
              <AtlassianEmail email={quoteAuthor} />
            </td>
          );
        case COLUMNS.UPDATED_BY:
          return (
            <td
              data-testid="quotes-updated-by"
              className="text-ellipsis"
              title={!updatedBy?.includes('atlassian.com') && updatedBy}
            >
              <AtlassianEmail email={updatedBy} />
            </td>
          );
        case COLUMNS.PARTNER_MARGIN:
          if (allItemsExternallyBilled) {
            return <td>{<NoData />}</td>;
          }

          return (
            <td className={classNames({ 'text-align-right': isDesignIteration1Enabled })}>
              <FormatCurrency
                showCurrencyCode
                showCommas
                value={defaultTo(getTotalPartnerMarginAmount(item, DiscountType.PARTNER_MARGIN, null, currency), 0)}
                currency={currency}
              />
            </td>
          );
      }
    });

    return rowData;
  };

  const onPageChange = useCallback(
    (pageNumber: number) => {
      setCurrentPage(pageNumber);
      refetch({
        searchOptions: {
          first: rowsPerPage,
          offset: (pageNumber - 1) * rowsPerPage,
          sort: sortOptions,
        },
        filterOptions: filterOptions.current,
      });
    },
    [refetch, rowsPerPage, sortOptions]
  );

  const handleRowsChange = useCallback(
    (rowsCount: number) => {
      setRowsPerPage(rowsCount);
      setCurrentPage(DEFAULT_PAGE);
      refetch({
        searchOptions: {
          first: rowsCount,
          offset: 0,
          sort: sortOptions,
        },
        filterOptions: filterOptions.current,
      });
    },
    [refetch, sortOptions]
  );

  const onSearch = useCallback(
    async (formData: FormData) => {
      let searchInput = formData?.searchTerm;
      searchInput = searchInput.trim();
      const contactEmailSearchInput = isEmail(searchInput) ? searchInput : null;
      const selectedStatusFilters = statusFilterOptions.length > 0 ? [...statusFilterOptions] : [];
      let emailFilterOptions;
      if (contactEmailSearchInput) {
        emailFilterOptions = await getResourceIdsFilterOptions(contactEmailSearchInput);
      } else {
        setFetchIdsError(undefined);
      }

      const newFilterOptions: FilterOption = await getFilterOptions(
        searchInput,
        selectedTab,
        [...createdDateRange, ...expiryDateRange, ...updatedDateRange],
        selectedStatusFilters,
        emailFilterOptions,
        currency
      );
      filterOptions.current = newFilterOptions;

      setSearchInputText(searchInput);
      setCurrentPage(DEFAULT_PAGE);
      refetch({
        filterOptions: newFilterOptions,
        searchOptions: {
          first: rowsPerPage,
          offset: 0,
          sort: sortOptions,
        },
      });
    },
    [
      refetch,
      rowsPerPage,
      selectedTab,
      sortOptions,
      createdDateRange,
      expiryDateRange,
      updatedDateRange,
      getFilterOptions,
      statusFilterOptions,
    ]
  );

  const handleDateRangeFilterChange = async (key: string, value: DateRangeType) => {
    const fromDate = {
      key: key + 'IsGte',
      value: value.from,
    };
    const toDate = {
      key: key + 'IsLte',
      value: value.to,
    };

    const dateRange = [fromDate, toDate];

    if (key === DATE_RANGE_KEYS.CREATED_DATE) {
      setCreatedDateRange(dateRange);
    } else if (key === DATE_RANGE_KEYS.EXPIRY_DATE) {
      setExpiryDateRange(dateRange);
    } else if (key === DATE_RANGE_KEYS.LAST_UPDATED) {
      setUpdatedDateRange(dateRange);
    }

    const selectedStatusFilters = statusFilterOptions.length > 0 ? [...statusFilterOptions] : [];

    const emailFilterOptions = await getEccSearchFilterOptions(searchInputText);

    const newFilterOptions: FilterOption = await getFilterOptions(
      searchInputText,
      selectedTab,
      dateRange,
      selectedStatusFilters,
      emailFilterOptions,
      currency
    );
    filterOptions.current = newFilterOptions;

    setCurrentPage(DEFAULT_PAGE);
    refetch({
      filterOptions: newFilterOptions,
      searchOptions: {
        first: rowsPerPage,
        offset: 0,
        sort: sortOptions,
      },
    });
  };
  const renderLastUpdatedFilter = () => {
    return (
      <div className="ml-12">
        <QuoteDateFilter
          placeholder="Last Updated"
          onDateRangeFilterChange={(range) => handleDateRangeFilterChange(DATE_RANGE_KEYS.LAST_UPDATED, range)}
          dateRangeKey={DATE_RANGE_KEYS.LAST_UPDATED}
          selectedValue={selectedUpdatedDateRangeValue}
          setSelectedValue={setSelectedUpdatedDateRangeValue}
        />
      </div>
    );
  };
  const renderCreatedDateFilter = () => {
    return (
      <div className="ml-12">
        <QuoteDateFilter
          placeholder="Created"
          onDateRangeFilterChange={(range) => handleDateRangeFilterChange(DATE_RANGE_KEYS.CREATED_DATE, range)}
          dateRangeKey={DATE_RANGE_KEYS.CREATED_DATE}
          selectedValue={selectedCreatedDateRangeValue}
          setSelectedValue={setSelectedCreatedDateRangeValue}
        />
      </div>
    );
  };

  const renderExpiryDateFilter = () => {
    return (
      <div className="ml-12">
        <QuoteDateFilter
          placeholder="Expires"
          onDateRangeFilterChange={(range) => handleDateRangeFilterChange(DATE_RANGE_KEYS.EXPIRY_DATE, range)}
          dateRangeOptions={expiryDateRangeOption}
          dateRangeKey={DATE_RANGE_KEYS.EXPIRY_DATE}
          selectedValue={selectedExpiryDateRangeValue}
          setSelectedValue={setSelectedExpiryDateRangeValue}
        />
      </div>
    );
  };

  /* since the status filter is always going to have "open" and "needs review" as default options,
    we can't use the length of the statusFilterOptions to determine if the filter is applied,
    instead we use the isStatusFilterApplied state based on the current selected status filters
  */
  const isAnyFilterApplied =
    expiryDateRange.length > 0 ||
    createdDateRange.length > 0 ||
    updatedDateRange.length > 0 ||
    isStatusFilterApplied ||
    isCurrencyFilterApplied ||
    (isDesignIteration1Enabled && isDefaultFiltersEnabled && isStatusFilterAppliedByDefault);

  const clearDateRangeFilters = () => {
    setCreatedDateRange([]);
    setExpiryDateRange([]);
    setUpdatedDateRange([]);
    setSelectedCreatedDateRangeValue('');
    setSelectedExpiryDateRangeValue('');
    setSelectedUpdatedDateRangeValue('');
  };

  const getEccSearchFilterOptions = async (searchInputText: string) => {
    const contactEmailSearchInput = isEmail(searchInputText) ? searchInputText : null;
    let emailFilterOptions;
    if (contactEmailSearchInput) {
      emailFilterOptions = await getResourceIdsFilterOptions(contactEmailSearchInput);
    }
    return emailFilterOptions;
  };
  const clearAllFilters = async () => {
    setCurrentPage(DEFAULT_PAGE);
    clearDateRangeFilters();
    setCurrency('');
    setStatusFilterResetCount(statusFilterResetCount + 1);
    setStatusFilterFields(getInitialStatusFilterFields(shouldHideTabs));
    setStatusFilterOptions([{ status: QuoteStatusType.OPEN }, { status: QuoteStatusType.STALE }]);
    setIsStatusFilterApplied(false);
    if (isDefaultFiltersEnabled) {
      setIsStatusFilterAppliedByDefault(false);
    }

    // Update the existing filters without removing the search input and selected tab filters.
    // getFilterOptions(searchInputText, selectedTab) will remove all the date range filters.

    const emailFilterOptions = await getEccSearchFilterOptions(searchInputText);

    const newFilterOptions: FilterOption = await getFilterOptions(
      searchInputText,
      selectedTab,
      [],
      [],
      emailFilterOptions
    );
    filterOptions.current = newFilterOptions;

    refetch({
      filterOptions: newFilterOptions,
      searchOptions: {
        first: rowsPerPage,
        offset: 0,
        sort: sortOptions,
      },
    });
  };

  const handleStatusFilter = async (
    selectedStatusFilters: { key: string; value: string }[] | Record<string, string>[]
  ) => {
    if (isDesignIteration1Enabled && isDefaultFiltersEnabled) {
      if (!selectedStatusFilters.length) {
        // this will not set default values in select field
        setStatusFilterResetCount((prev) => prev + 1);
      } else if (statusFilterResetCount) {
        // user must have clicked clear reset filters once, hence reseting the value for clear reset to work again
        setStatusFilterResetCount(0);
      }

      setIsStatusFilterAppliedByDefault(false);
    }

    const emailFilterOptions = await getEccSearchFilterOptions(searchInputText);

    const newFilterOptions: FilterOption = await getFilterOptions(
      searchInputText,
      selectedTab,
      [...createdDateRange, ...expiryDateRange, ...updatedDateRange],
      [...selectedStatusFilters],
      emailFilterOptions,
      currency
    );

    setIsStatusFilterApplied(selectedStatusFilters.length > 0);

    filterOptions.current = newFilterOptions;
    refetch({
      filterOptions: newFilterOptions,
      searchOptions: {
        first: rowsPerPage,
        offset: 0,
        sort: sortOptions,
      },
    });
  };

  const handleCurrencyFilter = async (selectedCurrency: string) => {
    const emailFilterOptions = await getEccSearchFilterOptions(searchInputText);

    const selectedStatusFilters = statusFilterOptions.length > 0 ? [...statusFilterOptions] : [];

    const newFilterOptions: FilterOption = await getFilterOptions(
      searchInputText,
      selectedTab,
      [...createdDateRange, ...expiryDateRange, ...updatedDateRange],
      selectedStatusFilters,
      emailFilterOptions,
      selectedCurrency
    );

    filterOptions.current = newFilterOptions;

    setCurrentPage(DEFAULT_PAGE);
    refetch({
      filterOptions: newFilterOptions,
      searchOptions: {
        first: rowsPerPage,
        offset: 0,
        sort: sortOptions,
      },
    });
  };

  const renderStatusFilter = () => {
    return (
      <CustomFilterCheckboxWrapper
        filterFields={statusFilterFields}
        handleFilterUpdate={handleStatusFilter}
        shouldHideClearFilterButton={true}
        resetFromParent={statusFilterResetCount}
        version={isDesignIteration1Enabled && isDefaultFiltersEnabled ? 2 : undefined}
      />
    );
  };

  const renderCurrencyFilter = () => {
    return (
      <div>
        <LimitCurrencyDropdown
          currency={currency}
          setCurrency={(value) => {
            const selectedCurrency = value ? (value as string) : '';
            setCurrency(selectedCurrency);
            handleCurrencyFilter(selectedCurrency);
          }}
          showLabel={false}
          isLightTheme
          isFilter
          showAllCurrency
          shouldPrependCurrencyText={isDesignIteration1Enabled}
          isDesignIteration1Enabled={isDesignIteration1Enabled}
        />
      </div>
    );
  };

  const renderFilters = (tabLabel?: string) => {
    const searchPlaceholder = isDesignIteration1Enabled ? 'Search quotes' : 'Search';
    const searchHelperMessage = ' customer contact, quote contact,';
    const helperMessage = `Enter QT-#, company, sold-to address,${searchHelperMessage} or quote name`;

    return (
      <div
        className={classNames({
          'ppc-quote-table-filter flex': !isDesignIteration1Enabled,
          'ppc-quote-table-filter-v2 flex': isDesignIteration1Enabled,
        })}
      >
        <CustomSearch
          placeholder={searchPlaceholder}
          onSearch={onSearch}
          isLoading={loading}
          isClearable={true}
          helperMessage={helperMessage}
        />
        {tabLabel === TAB_ENUM.OPEN && renderStatusFilter()}
        {renderLastUpdatedFilter()}
        {renderCreatedDateFilter()}
        {renderExpiryDateFilter()}
        {isMultiCurrencyChangesEnabled && renderCurrencyFilter()}
        {isAnyFilterApplied && (
          <Button appearance="link" testId="filter-reset-link" onClick={clearAllFilters}>
            Clear all filters
          </Button>
        )}
      </div>
    );
  };

  const renderRequestQuoteCTA = (testId?: string, appearance?: Appearance) => {
    if (defaultSearchFilterOptions) return null;
    const requestQuoteLink = getPartnerServiceDeskUrlForNewQuoteHelp();
    const dataTestId = testId ?? 'request-quote-cta';

    return (
      <LoadingButton
        testId={dataTestId}
        onClick={() => {
          window.open(requestQuoteLink, '_blank');
        }}
        isDisabled={shouldDisableRequestQuoteCta}
        {...(appearance && { appearance: appearance })}
      >
        Request quote
      </LoadingButton>
    );
  };

  const renderQuoteCTA = () => {
    if (shouldHideActionsMenu) {
      return null;
    }

    const renderExportCSV = (appearance?: Appearance) => {
      return (
        <LoadingButton
          testId="download-csv-btn"
          onClick={() => {
            downloadQuotesListCSV();
          }}
          isLoading={quotesListLoading}
          appearance={appearance || 'default'}
          iconBefore={<ExportIcon label="Export icon" size="small" />}
        >
          Export as CSV
        </LoadingButton>
      );
    };

    const renderQuoteHeaderActions = () => {
      return (
        <PopupMenuGroup minWidth={100}>
          <Section>
            <div className="quote-list-header-popup">
              {isDesignIteration1Enabled || renderExportCSV('subtle')}

              {/* Render Request quote CTA only when there are results */}
              {hasNoResults || renderRequestQuoteCTA('request-quote-cta-in-header', 'subtle')}
            </div>
          </Section>
        </PopupMenuGroup>
      );
    };

    return (
      <>
        {isDesignIteration1Enabled && renderExportCSV()}
        <Popup
          testId="quote-header-action"
          isOpen={isOpen}
          content={renderQuoteHeaderActions}
          placement={'bottom-end'}
          onClose={() => setIsOpen(false)}
          trigger={(triggerProps) => (
            <Button
              {...triggerProps}
              isSelected={isOpen}
              onClick={() => setIsOpen(!isOpen)}
              iconBefore={<MoreIcon label="More" />}
              testId="quote-header-action-trigger"
              className={isDesignIteration1Enabled ? 'ml-8' : ''}
            />
          )}
        />
      </>
    );
  };

  const renderTabTable = (tabLabel: string, tableClass?: string) => {
    const children = (
      <>
        <ColGroup columns={getSelectedColumns(columnsList)} />
        <TableHead columns={getSelectedColumns(columnsList)} />
        <TableBody>
          {data?.map((item: any) => {
            return (
              <tr
                key={item.id + tabLabel}
                className={classNames({ 'needs-review-row': isNeedsReviewStatus(item.status) })}
              >
                {getRowData(item)}
              </tr>
            );
          })}
        </TableBody>
      </>
    );

    const isSearchOrFilterApplied = searchInputText || isAnyFilterApplied || isSearchTermPresent;

    const emptyStatePropsForSearch = isDesignIteration1Enabled
      ? emptyStatePropForWrongSearch
      : getEmptyStatePropQuoteTable(undefined, true);

    const emptyStateProps = isDesignIteration1Enabled
      ? emptyStatePropForQuoteTable(renderRequestQuoteCTA())
      : getEmptyStatePropQuoteTable(renderRequestQuoteCTA());

    return (
      <CustomDynamicResultTable
        className={classNames('ppc-quote-table', tableClass, {
          'ppc-quote-table-v2': isDesignIteration1Enabled,
        })}
        emptyStateProps={isSearchOrFilterApplied ? emptyStatePropsForSearch : emptyStateProps}
        customiseColumnView={
          shouldHideCustomiseView ? (
            <></>
          ) : (
            <CustomizeView
              columns={columnsList}
              columnCount={columnsList.length}
              updatedColumnView={(record: Array<HeadCell>) => setColumnList([...record] as Array<ColumnList>)}
            />
          )
        }
        isLoading={loading || isIdsLoading}
        noResults={hasNoResults || Boolean(fetchIdsError)}
        error={error ?? fetchIdsError}
        totalCount={totalCount}
        currentPage={currentPage}
        rowsPerPage={rowsPerPage}
        onPageChange={onPageChange}
        onRowChange={handleRowsChange}
        isPaginationEnabled={totalCount > DEFAULT_PAGE_SIZE}
        shouldShowHorizontalScrollAnimation={true}
        hasTableWrapper={true}
        shouldForceEnableFilters={true}
        filters={renderFilters(tabLabel)}
        actions={isDesignIteration1Enabled && [renderQuoteCTA]}
        errorStateProps={errorStateProp}
        isSearched={!!searchInputText}
        isAnyFilterApplied={isAnyFilterApplied}
        loaderOptions={shouldHideFilters ? SEARCH_PAGE_LOADER_OPTIONS : { filtersCount: 4 }}
      >
        {children}
      </CustomDynamicResultTable>
    );
  };

  const resetSortedRefs = () => {
    isCreatedDescSortedRef.current = defaultIsCreatedDescSorted;
    isExpiryAscSortedRef.current = undefined;
    isTotalAmountAscSortedRef.current = undefined;
    isUpdatedDateAscSortedRef.current = undefined;
    isCompanyNameAscSortedRef.current = undefined;
  };

  const onTabSelect = async (tabLabel: string) => {
    resetSortedRefs();
    clearDateRangeFilters();
    setSelectedTab(tabLabel);
    setIsStatusFilterApplied(false);
    setCurrency('');

    const newFilterOptions: FilterOption = await getFilterOptions('', tabLabel);
    filterOptions.current = newFilterOptions;

    setCurrentPage(DEFAULT_PAGE);
    setSortOptions(defaultSortOptions); // Reset sort to default value

    refetch({
      filterOptions: newFilterOptions,
      searchOptions: {
        first: rowsPerPage,
        offset: 0,
        sort: defaultSortOptions, // Refetch with default sort value
      },
    });
  };

  const renderTabPanels = () => {
    return Object.values(TAB_ENUM).map((key) => {
      return (
        <TabPanel label={key} key={key}>
          {renderTabTable(key)}
        </TabPanel>
      );
    });
  };

  return (
    <div
      className={classNames({
        'tab-container-quote': !isDesignIteration1Enabled,
        'tab-container-quote-with-label': label,
      })}
    >
      <div className="heading-container">
        {label && !isDesignIteration1Enabled && <TableHeading className="pt-32 ph-28">{label}</TableHeading>}
        {isDesignIteration1Enabled || renderQuoteCTA()}
      </div>
      {shouldHideTabs ? (
        renderTabTable(TAB_ENUM.OPEN, 'without-tabs')
      ) : (
        <Tabs testId="quote-table-tabs" onTabSelect={onTabSelect} selectedTabKey={kebabCase(selectedTab)}>
          {renderTabPanels()}
        </Tabs>
      )}
    </div>
  );
};
